import { Flex, Text, Button, Icon, Box, Checkbox } from "@chakra-ui/react";
import { MainModal } from "../MainModal/MainModal";
import React, { useState } from "react";
import { RecruitmentChannel } from "@/model/recruitment";
import { Department } from "@/model/department";
import { ChevronRightIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { ApplicationManageChannelForm, ApplicationChanelDepartment } from "@/model/applications";
import api from "../../api";
import { ApplicationManageChannelRequest } from "@/api/applications/types";

interface AssignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: (payload: any) => void;
  applicationId?: number;
  applicationName?: string;
  customerName?: string;
  channels?: (RecruitmentChannel & { departments?: Department[] })[];
  assigned?: ApplicationChanelDepartment[];
}

export const AssignModal = ({
  isOpen,
  onClose,
  onConfirm,
  applicationId,
  applicationName,
  customerName,
  channels = [],
  assigned = [],
}: AssignModalProps) => {
  const [selected, setSelected] = useState<number[]>([]);
  const [expanded, setExpanded] = useState<number[]>([]);
  const [selectedDepartmentsByChannel, setSelectedDepartmentsByChannel] = useState<Record<number, number[]>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize selection from assigned and preselect departments when modal opens or data changes
  React.useEffect(() => {
    if (!isOpen) return;
    const assignedIds = (assigned || []).map((a) => a.id);
    const nextSelected = Array.from(new Set(assignedIds));
    const nextDeps: Record<number, number[]> = {};
    if (channels && channels.length > 0) {
      for (const a of assigned as ApplicationChanelDepartment[]) {
        const ch = channels.find((c) => c.id === a.id);
        if (!ch) continue;
        const availableDepIds = Array.isArray(ch.departments) ? ch.departments.map((d) => d.id) : [];
        const initialDepIds = Array.isArray(a.departments) ? a.departments : [];
        nextDeps[a.id] = initialDepIds.filter((depId) => availableDepIds.includes(depId));
      }
    }
    const validSelected = nextSelected.filter((id) => (nextDeps[id]?.length ?? 0) > 0);
    setSelected(validSelected);
    setSelectedDepartmentsByChannel(nextDeps);
  }, [isOpen, JSON.stringify(assigned), JSON.stringify(channels?.map((c) => ({ id: c.id, deps: c.departments?.map((d) => d.id) })))]);

  const toggle = (id: number) => {
    const channel = channels.find((c) => c.id === id);
    const depIds = Array.isArray(channel?.departments) ? channel!.departments!.map((d) => d.id) : [];
    setSelected((prev) => {
      if (prev.includes(id)) {
        setSelectedDepartmentsByChannel((deps) => {
          const next = { ...deps };
          delete next[id];
          return next;
        });
        setExpanded((exp) => exp.filter((v) => v !== id));
        return prev.filter((s) => s !== id);
      }
      if (depIds.length === 0) {
        return prev;
      }
      setSelectedDepartmentsByChannel((deps) => ({ ...deps, [id]: depIds }));
      setExpanded((exp) => (exp.includes(id) ? exp : [...exp, id]));
      return [...prev, id];
    });
  };

  const toggleExpand = (id: number) => {
    setExpanded((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  };

  const toggleDepartment = (channelId: number, departmentId: number) => {
    setSelectedDepartmentsByChannel((prev) => {
      const current = prev[channelId] ?? [];
      const exists = current.includes(departmentId);
      const nextForChannel = exists ? current.filter((v) => v !== departmentId) : [...current, departmentId];
      const next = { ...prev } as Record<number, number[]>;
      if (nextForChannel.length > 0) {
        next[channelId] = nextForChannel;
      } else {
        delete next[channelId];
      }
      setSelected((selPrev) => {
        if (nextForChannel.length > 0) {
          return selPrev.includes(channelId) ? selPrev : [...selPrev, channelId];
        }
        return selPrev.filter((v) => v !== channelId);
      });
      return next;
    });
    setExpanded((exp) => (exp.includes(channelId) ? exp : [...exp, channelId]));
  };

  const selectedCount = React.useMemo(
    () => selected.filter((id) => (selectedDepartmentsByChannel[id]?.length ?? 0) > 0).length,
    [selected, selectedDepartmentsByChannel],
  );

  return (
    <MainModal isOpen={isOpen} onClose={onClose} title="Управление каналами подбора" width="840px" showFooter={false}>
      <Flex direction="column" gap="20px">
        <Box borderRadius="12px" bg="#F8FAFC" p="16px">
          <Text fontSize="14px" color="#6B7280">
            Заявка:
          </Text>
          <Text fontSize="18px" color="#111827" fontWeight={700}>
            {applicationName || "—"}
          </Text>
          <Text fontSize="16px" color="#111827">
            {customerName || "—"}
          </Text>
        </Box>

        <Text fontSize="18px" color="#111827" fontWeight={700}>
          Выберите каналы подбора:
        </Text>

        <Flex direction="column" gap="16px">
          {channels.map((ch) => (
            <Flex key={ch.id} direction="column" gap="8px">
              <Flex alignItems="center" gap="12px">
                <Checkbox
                  isChecked={selected.includes(ch.id)}
                  isDisabled={!((ch?.departments?.length ?? 0) > 0)}
                  onChange={() => toggle(ch.id)}
                  size="lg"
                />
                <Icon
                  as={expanded.includes(ch.id) ? ChevronDownIcon : ChevronRightIcon}
                  boxSize={5}
                  cursor="pointer"
                  onClick={() => toggleExpand(ch.id)}
                />
                <Text fontSize="18px" color="#111827" fontWeight={700} cursor="pointer" onClick={() => toggleExpand(ch.id)}>
                  {ch.name}
                </Text>
                <Text fontSize="16px" color="#6B7280">
                  {ch?.departments?.length ?? 0} подразделений
                </Text>
              </Flex>
              {expanded.includes(ch.id) && !!ch?.departments?.length && (
                <Flex direction="column" gap="8px" pl="44px">
                  {ch.departments!.map((dep: Department) => (
                    <Flex key={dep.id} alignItems="center" gap="8px">
                      <Checkbox
                        isChecked={!!selectedDepartmentsByChannel[ch.id]?.includes(dep.id)}
                        onChange={() => toggleDepartment(ch.id, dep.id)}
                      />
                      <Text fontSize="16px" color="#111827">
                        {dep.name}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
              )}
            </Flex>
          ))}
        </Flex>

        <Flex justifyContent="flex-end" gap="12px" mt="8px">
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button
            colorScheme="blue"
            isLoading={isSubmitting}
            isDisabled={!applicationId}
            onClick={async () => {
              if (!applicationId) return;
              try {
                setIsSubmitting(true);
                const channelsToSend = selected
                  .filter((channelId) => (selectedDepartmentsByChannel[channelId]?.length ?? 0) > 0)
                  .map((channelId) => ({ id: channelId, departments: selectedDepartmentsByChannel[channelId] }));
                const form: ApplicationManageChannelForm = { channels: channelsToSend };
                const req: ApplicationManageChannelRequest = {
                  params: { id: applicationId },
                  body: form,
                };
                await api.applications.applicationManageChannel(req);
                onConfirm?.(req);
                onClose();
              } catch (e) {
                console.error(e);
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {`Сохранить (${selectedCount})`}
          </Button>
        </Flex>
      </Flex>
    </MainModal>
  );
};
